import { useState } from 'react';
import { FileDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/lib/hooks/use-toast';
import axiosInstance from '@/lib/api/axios-instance';
import { tradeService } from '../services/trade-service';

/**
 * Descarga el PDF de la cotización — lo que la vendedora le manda al cliente.
 *
 * No es un `<a href>` ni un `window.open`: el endpoint pide el token del
 * usuario, y abrir la URL en una pestaña nueva no lleva la cabecera de
 * autorización. Se baja por el cliente HTTP, que sí la pone, y recién después se
 * dispara la descarga desde el blob.
 */

export interface QuotePdfButtonProps {
  orderId: string;
  orderNumber: string;
  /** `icon` para la tabla, `full` para el detalle. */
  variant?: 'icon' | 'full';
}

export function QuotePdfButton({ orderId, orderNumber, variant = 'icon' }: QuotePdfButtonProps) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const response = await axiosInstance.get(tradeService.quotePdfUrl(orderId), {
        responseType: 'blob',
      });

      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${orderNumber}-cotizacion.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Sin esto el blob queda en memoria hasta que se recarga la página.
      URL.revokeObjectURL(url);
    } catch {
      toast({
        title: 'No se pudo generar el PDF',
        description: 'Revisá que la cotización tenga sus tramos cargados.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'icon') {
    return (
      <Button
        size="sm"
        variant="ghost"
        className="h-7 w-7 p-0"
        title="Descargar el PDF para el cliente"
        aria-label={`Descargar el PDF de ${orderNumber}`}
        disabled={busy}
        onClick={() => void download()}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <FileDown className="h-3.5 w-3.5" />
        )}
      </Button>
    );
  }

  return (
    <Button variant="outline" size="sm" disabled={busy} onClick={() => void download()}>
      {busy ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <FileDown className="mr-2 h-4 w-4" />
      )}
      PDF para el cliente
    </Button>
  );
}
